import { cn } from "../../../lib/cn";
import { Button } from "../../shadcn/button";
import { NightPreview, formatDuration, toMinutes } from "./nightClock";

export type SummaryStatus = "new" | "dirty" | "saved";

export type SummaryMetrics = {
  asleepMinutes: number | null;
  inBedMinutes: number | null;
  efficiencyPct: number | null;
} | null;

export type NightBarProps = {
  preview: NightPreview;
  awakeningCount: number | null;
  awakeningMinutes: number;
  awakeningTimes: string[];
};

export type NightSummaryProps = {
  title: string;
  status: SummaryStatus;
  metrics: SummaryMetrics;
  bar: NightBarProps;
  done: Set<number>;
  canSave: boolean;
  saving: boolean;
  statusText: string;
  onSave(): void;
  /** The 7 nights ending last night, oldest first: saved or not. */
  last7: boolean[];
};

// The bar spans 9 pm of the night's day to 9 am the next morning, in minutes from 00:00 of the day.
const BAR_START = 21 * 60;
const BAR_SPAN = 12 * 60;
const pct = (m: number) => Math.min(Math.max(((m - BAR_START) / BAR_SPAN) * 100, 0), 100);
const TICKS = ["9 pm", "12 am", "3 am", "6 am", "9 am"];

/** In-bed and asleep spans, a white gap per placed awakening, hatching for awake time with no clock (mockup-v4:600-622). */
export function NightBar({ preview, awakeningCount, awakeningMinutes, awakeningTimes }: NightBarProps) {
  const count = awakeningCount ?? 0;
  const perWake = awakeningMinutes / Math.max(count, 1);
  const unplaced = Math.round(perWake * Math.max(count - awakeningTimes.length, 0));
  const seg = "absolute transition-[left,width] duration-200 ease-soft motion-reduce:transition-none";
  const span = (from: number, to: number) => ({ left: `${pct(from)}%`, width: `${Math.max(pct(to) - pct(from), 0)}%` });
  return (
    <div>
      <div className="relative mt-5 h-6 overflow-hidden rounded-md bg-neutral-100" aria-hidden>
        {preview.ok && (
          <>
            <div data-segment="in-bed" className={cn(seg, "inset-y-0 bg-brand-mid")} style={span(preview.inBedAt, preview.outAt)} />
            <div
              data-segment="asleep"
              className={cn(seg, "inset-y-[5px] rounded-[3px] bg-brand")}
              style={{
                ...span(preview.asleepAt, preview.wakeAt),
                ...(unplaced > 0 ? { background: "repeating-linear-gradient(135deg, #5754FF 0 6px, #8b89ff 6px 9px)" } : {}),
              }}
            />
            {awakeningTimes.map((t, i) => {
              const at = toMinutes(t) < 720 ? toMinutes(t) + 1440 : toMinutes(t);
              return <div key={i} data-segment="awake" className={cn(seg, "inset-y-[5px] bg-white")} style={span(at, at + perWake)} />;
            })}
          </>
        )}
      </div>
      <div className="mt-1 flex justify-between text-[11px] tabular-nums text-neutral-400">
        {TICKS.map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>
      {unplaced > 0 && <div className="mt-2 text-xs text-neutral-500">Includes {unplaced} min awake at times not given.</div>}
    </div>
  );
}

/** A value that fades in (opacity only) each time it changes. */
const Value = ({ text, className }: { text: string; className: string }) => (
  <span key={text} className={cn("tabular-nums animate-fade-in", className)}>
    {text}
  </span>
);

const BADGE: Record<SummaryStatus, string> = {
  new: "Not saved yet",
  dirty: "Unsaved changes",
  saved: "Saved",
};

/** The save button and the status line, shared by the rail and the phone's bottom bar. */
export function SaveControl({ canSave, saving, statusText, onSave }: Pick<NightSummaryProps, "canSave" | "saving" | "statusText" | "onSave">) {
  return (
    <>
      <Button type="button" disabled={!canSave} onClick={onSave}>
        {saving ? "Saving…" : "Save night"}
      </Button>
      <div className="mt-2 text-center text-[13px] text-neutral-500">{statusText}</div>
    </>
  );
}

/** The summary rail: night, status, metrics, night bar, save, last 7 nights (mockup-v4:338-370). */
export function NightSummary({ title, status, metrics, bar, done, canSave, saving, statusText, onSave, last7 }: NightSummaryProps) {
  const fmt = (n: number | null | undefined) => (n === null || n === undefined ? "-" : formatDuration(n));
  const eff = metrics?.efficiencyPct;
  const savedCount = last7.filter(Boolean).length;
  return (
    <div className="rounded-[12px] border border-neutral-200 bg-white p-5">
      <div className="text-[13px] text-neutral-500">Night of</div>
      <div className="mt-0.5 text-base font-semibold">{title}</div>
      <span
        className={cn(
          "mt-3 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors duration-150 ease-soft",
          status === "saved" ? "border-green-200 bg-green-50 text-green-600" : "border-neutral-200 text-neutral-500"
        )}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
        {BADGE[status]}
      </span>
      <div className="mt-4 flex gap-1" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={cn("h-1 flex-1 rounded-sm transition-colors duration-200 ease-soft", done.has(i) ? "bg-neutral-950" : "bg-neutral-100")}
          />
        ))}
      </div>

      <div className="mt-5 flex items-baseline gap-2">
        <Value text={fmt(metrics?.asleepMinutes)} className="text-[32px] font-semibold leading-10 tracking-[-0.02em]" />
        <span className="text-neutral-500">asleep</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-neutral-500">In bed</div>
          <Value text={fmt(metrics?.inBedMinutes)} className="text-base font-semibold" />
        </div>
        <div>
          <div className="text-xs text-neutral-500">Efficiency</div>
          <Value text={eff === null || eff === undefined ? "-" : `${eff}%`} className="text-base font-semibold" />
        </div>
      </div>
      <NightBar {...bar} />

      <div className="hidden min-[1100px]:block">
        <div className="my-5 h-px bg-neutral-200" />
        <SaveControl canSave={canSave} saving={saving} statusText={statusText} onSave={onSave} />
      </div>

      <div className="my-5 h-px bg-neutral-200" />
      <div className="flex justify-between text-xs text-neutral-500">
        <span>Last 7 nights</span>
        <span>{savedCount} of 7</span>
      </div>
      <div className="mt-2 flex gap-1" aria-hidden>
        {last7.map((on, i) => (
          <span
            key={i}
            className={cn("h-1.5 flex-1 rounded-[3px] transition-colors duration-150 ease-soft", on ? "bg-green-600" : "bg-neutral-100")}
          />
        ))}
      </div>
    </div>
  );
}
